// @ts-nocheck
import React, { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Plus,
  Download,
  MoreHorizontal,
  Activity,
  User,
  CheckCircle2,
  Loader2,
  Trash2,
  AlertOctagon,
  AlertTriangle,
  Coins,
  ShieldCheck,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn, formatNumber } from "@/lib/utils";
import { trpcClient } from "@/lib/trpc";
import { useNcrDispositions } from "@/hooks/useNcrDispositions";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BulkActionBar,
  type BulkAction,
} from "@/components/shared/BulkActionBar";
import type { NCRItem } from "@/shared/schemas";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import { type StatusSidebarItem } from "@/components/shared/StatusSidebar";
import { NCRStatus } from "@/config/status-registry";
import { useRowSelection } from "@/hooks/useRowSelection";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import {
  CompactKpiRibbon,
  type CompactKpiItem,
} from "@/components/shared/CompactKpiRibbon";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { useConfirm } from "@/providers/ConfirmProvider";
import { useExcelExport } from "@/hooks/useExcelExport";
import { downloadExcelTemplate, type ExcelColumnDef } from "@/lib/excel.utils";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import { InlineFilterRow, CHECKBOX_COL_NULL } from "@/components/shared/InlineFilterRow";
import type { FilterField } from "@/components/shared/FilterBar";
import { emsTabClass } from "@/components/ui/ems-tabs";

const STATUS_ITEMS: StatusSidebarItem[] = Object.entries(NCRStatus).map(
  ([key, cfg]) => ({
    key,
    label: cfg.label,
    color: cfg.color,
    dotColor: cfg.dotColor,
  }),
);

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "ncrNumber", label: "Mã NCR", width: 16 },
  { key: "title", label: "Tiêu Đề Sự Cố", width: 25 },
  { key: "description", label: "Mô Tả Chi Tiết", width: 30 },
  { key: "source", label: "Nguồn Phát Hiện", width: 18 },
  { key: "severity", label: "Mức Độ", width: 14 },
  { key: "status", label: "Trạng Thái", width: 16 },
  { key: "responsibleDept", label: "Bộ Phận Chịu Trách Nhiệm", width: 22 },
  {
    key: "costImpact",
    label: "Thiệt Hại (VNĐ)",
    width: 18,
    format: "number",
    formatter: (_v, row: any) => row.costImpact || 0,
  },
  {
    key: "createdAt",
    label: "Ngày Tạo",
    width: 14,
    formatter: (_v, row: any) => row.createdAt ? new Date(row.createdAt).toLocaleDateString("vi-VN") : "",
  },
];

const FILTER_FIELDS: FilterField[] = [
  { key: "ncrNumber", label: "Mã NCR", type: "text" },
  { key: "title", label: "Sự cố", type: "text" },
  { key: "responsibleDept", label: "Bộ phận", type: "text" },
  {
    key: "severity",
    label: "Mức độ",
    type: "select",
    options: [
      { value: "LOW", label: "Thấp" },
      { value: "MEDIUM", label: "Trung bình" },
      { value: "HIGH", label: "Cao" },
      { value: "CRITICAL", label: "Nghiêm trọng" },
    ],
  },
];

export default function NCRPage() {
  const dispositions = useNcrDispositions();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { hasPermission } = useRoleAccess();
  const queryClient = useQueryClient();

  // Đóng NCR ngay trên danh sách: máy chủ kiểm quyền duyệt NCR và đủ nguyên nhân / khắc phục / cách xử lý, báo lý do nếu chưa đủ
  const canCloseNcr = hasPermission("ncr", "approve");
  const NCR_CLOSABLE = ["OPEN", "INVESTIGATING", "VERIFICATION"];
  const handleCloseNcr = async (id: string, code: string) => {
    const ok = await confirm({
      title: "Đóng NCR",
      message: `Đóng phiếu ${code}? Phiếu đã đóng không mở lại được.`,
      confirmText: "Đóng NCR",
    });
    if (!ok) return;
    try {
      await (trpcClient as any).ncr.update.mutate({ id, data: { status: "CLOSED" } });
      toast.success(`Đã đóng ${code}`);
      await queryClient.invalidateQueries({ queryKey: ["ncr-all"] });
      await queryClient.invalidateQueries({ queryKey: ["ncr-stats"] });
    } catch (err: any) {
      toast.error(err?.message || "Không đóng được NCR");
    }
  };

  const {
    items,
    isLoading,
    error,
    currentPage,
    pageSize,
    statusFilter,
    onStatusChange,
    totalItems,
    totalPages,
    onPageChange,
    onPageSizeChange,
  } = useServerListFilter({
    queryKey: "ncr-all",
    queryFn: async (params) => {
      const d = await trpcClient.ncr.list.query({
        page: params.page,
        limit: params.limit,
        search: params.search,
        status: params.status,
      });
      return d as any;
    },
    defaultLimit: 20,
  });

  // Fetch stats for 4 KPI cards (including COPQ)
  const { data: statsData } = useQuery({
    queryKey: ["ncr-stats"],
    queryFn: async () => (trpcClient as any).ncr.getStats.query(),
  });

  const rowIds = (items as NCRItem[])
    .map((i: NCRItem) => i.ncrId || i.id || "")
    .filter(Boolean);
  const rowSelection = useRowSelection(rowIds);
  const {
    selectedIds,
    selectionMode,
    toggleSelect,
    toggleSelectAll,
    handleRowMouseDown,
    handleRowMouseUp,
    handleRowMouseLeave,
    wasLongPress,
    allSelected,
    handleRowSelect,
    clearSelection,
    selectedCount,
  } = rowSelection;

  const getBulkActions = (): BulkAction[] => {
    if (!selectedCount) return [];
    const actions: BulkAction[] = [];
    if (hasPermission("ncr", "delete")) {
      actions.push({
        label: "Xoá",
        icon: <Trash2 className="w-3.5 h-3.5" />,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            actionFn: async (id) => trpcClient.ncr.delete.mutate({ id }),
            actionName: "xóa",
            itemType: "NCR",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["ncr-all"] });
            },
            requireConfirm: true,
            confirmMessage: `Bạn có chắc muốn xóa ${selectedCount} NCR đã chọn?`,
          });
        },
        variant: "destructive",
      });
    }
    actions.push({
      label: "Xuất Excel",
      icon: <Download className="w-3.5 h-3.5" />,
      onClick: () => {
        handleExport();
        clearSelection();
      },
    });
    return actions;
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity?.toUpperCase()) {
      case "HIGH":
      case "CRITICAL":
        return (
          <Badge variant="destructive" className="h-5 px-1.5 text-[10px]">
            {severity}
          </Badge>
        );
      case "MEDIUM":
        return (
          <Badge className="bg-amber-100 text-amber-800 border-none h-5 px-1.5 text-[10px]">
            Medium
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
            {severity || "Low"}
          </Badge>
        );
    }
  };

  const stats = {
    total: statsData?.total ?? (totalItems || items.length),
    investigating:
      statsData?.investigatingCount ??
      statsData?.investigating ??
      items.filter((x: any) => ["INVESTIGATING", "OPEN", "investigating", "open"].includes(x.status)).length,
    correctiveAction:
      statsData?.correctiveCount ??
      statsData?.correctiveAction ??
      items.filter((x: any) => ["CORRECTIVE_ACTION", "VERIFICATION", "corrective_action", "verification"].includes(x.status)).length,
    closed:
      statsData?.closedCount ??
      statsData?.closed ??
      items.filter((x: any) => ["CLOSED", "closed"].includes(x.status)).length,
    totalCost: statsData?.totalCostImpact ?? statsData?.totalCost ?? 0,
  };

  const ncrFilterTags = React.useMemo(() => [
    { key: "ALL", label: "Tất cả sự cố", count: stats.total, color: "slate" as const },
    { key: "OPEN", label: "Mới mở", count: items.filter((x: any) => String(x.status).toUpperCase() === "OPEN").length, color: "rose" as const },
    { key: "INVESTIGATING", label: "Đang điều tra", count: stats.investigating, color: "amber" as const },
    { key: "CORRECTIVE_ACTION", label: "Khắc phục CAPA", count: stats.correctiveAction, color: "blue" as const },
    { key: "CLOSED", label: "Đã đóng", count: stats.closed, color: "emerald" as const },
  ], [items, stats]);

  const [showFilter, setShowFilter] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});

  const handleFilterChange = (key: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearAll = () => {
    setFilterValues({});
    onStatusChange(undefined as any);
  };

  const { handleExport, isExporting } = useExcelExport({
    filename: "danh_sach_ncr",
    columns: EXPORT_COLUMNS,
    serverQuery: async (filters) => {
      const res = await trpcClient.ncr.list.query({
        page: 1,
        limit: 5000,
        search: filters.search,
        status: statusFilter,
      });
      return (res as any)?.items || (res as any) || [];
    },
    filterValues,
    localData: items as any[],
    selectedIds: Array.from(selectedIds),
  });

  return (
    <ListPageShell
        trashModule="ncr"
      unitLabel="NCR"
      filterFields={FILTER_FIELDS}
      filterValues={filterValues}
      showFilter={showFilter}
      onToggleFilter={() => setShowFilter((prev) => !prev)}
      onClearAll={handleClearAll}
      tabs={
        <div className="flex items-center gap-4">
          <button
            type="button"
            className={emsTabClass(true, { size: "sm" })}
          >
            <AlertOctagon className="w-3.5 h-3.5 text-rose-600" />
            <span>Biên Bản NCR</span>
            <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-primary/10 text-primary tabular-nums">
              {totalItems}
            </span>
          </button>
        </div>
      }
      headerFilters={
        <div className="flex items-center gap-2 flex-wrap">
          <PageFilterTags
            items={ncrFilterTags}
            selectedKeys={!statusFilter || statusFilter === "ALL" ? [] : [statusFilter]}
            onSelect={(key) => onStatusChange(!key || key === "ALL" ? undefined : key)}
            dropdownLabel="Trạng thái NCR"
          />
        </div>
      }
      actions={
        <ExportImportButtons
          onExport={() => handleExport()}
          isExporting={isExporting}
          onDownloadTemplate={() => downloadExcelTemplate(EXPORT_COLUMNS, "mau_danh_sach_ncr")}
        >
          {hasPermission("ncr", "create") && (
            <Button
              size="sm"
              className="h-8 text-xs bg-rose-600 hover:bg-rose-700"
              onClick={() => navigate({ to: "/ncr/create" as any })}
            >
              <Plus className="mr-2 h-3 w-3" /> Tạo NCR Mới
            </Button>
          )}
        </ExportImportButtons>
      }
      currentPage={currentPage}
      pageSize={pageSize}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
    >
      <Table className={cn("min-w-[1250px]", selectionMode && "apv-select-mode")}>
        <TableHeader className="apv-table-header">
          <TableRow>
            <TableHead className={cn("apv-col-checkbox w-10 text-center px-4 whitespace-nowrap")}>
              <DataTableSelectAllCheckbox
                rowSelection={
                  { allSelected, toggleSelectAll, selectionMode } as any
                }
                asChild
              />
            </TableHead>
            <TableHead className="apv-col-code font-semibold w-[160px] whitespace-nowrap">Mã NCR</TableHead>
            <TableHead className="font-semibold min-w-[280px] whitespace-nowrap">Sự cố &amp; Nguyên nhân</TableHead>
            <TableHead className="font-semibold min-w-[180px] whitespace-nowrap">
              Trách nhiệm &amp; Thiệt hại
            </TableHead>
            <TableHead className="text-center font-semibold w-[120px] whitespace-nowrap">Mức độ</TableHead>
            <TableHead className="apv-col-actions w-[60px] text-right whitespace-nowrap"></TableHead>
          </TableRow>
          {showFilter && (
            <InlineFilterRow
              columns={[
                CHECKBOX_COL_NULL,
                { key: 'ncrNumber' },
                { key: 'title' },
                { key: 'responsibleDept' },
                { key: 'severity' },
                null,
              ]}
              fields={FILTER_FIELDS}
              values={filterValues}
              onChange={handleFilterChange}
            />
          )}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell
                colSpan={6}
                className="h-32 text-center text-slate-400"
              >
                <Loader2 className="h-5 w-5 animate-spin mx-auto text-indigo-500 mb-2" />{" "}
                Đang tải...
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={6} className="h-32 text-center text-rose-500">
                Lỗi khi tải dữ liệu.
              </TableCell>
            </TableRow>
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={6}
                className="h-32 text-center text-slate-400"
              >
                Không có NCR nào
              </TableCell>
            </TableRow>
          ) : (
            (items as NCRItem[]).map((item: any) => {
              const itemId = item.id || item.ncrId || "";
              const displayCode = item.ncrId || item.id || "";
              if (!itemId) return null;
              return (
                <DataTableRow
                  key={itemId}
                  id={itemId}
                  rowSelection={
                    {
                      selectedIds,
                      handleRowMouseDown,
                      handleRowMouseUp,
                      handleRowMouseLeave,
                      wasLongPress,
                      selectionMode,
                      handleRowSelect,
                    } as any
                  }
                  onClick={(e) => {
                    if (selectionMode) return;
                    navigate({
                      to: "/ncr/$ncrId" as any,
                      params: { ncrId: itemId },
                    } as any);
                  }}
                >
                  <TableCell
                    className={cn("px-4 apv-col-checkbox")}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DataTableRowCheckbox
                      id={itemId}
                      rowSelection={{ selectedIds, toggleSelect } as any}
                      asChild
                    />
                  </TableCell>
                  <TableCell className="apv-col-code py-1.5">
                    <div className="flex flex-col gap-1.5 items-start">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-bold text-rose-600 text-[13px] leading-none group-hover:underline">
                          {displayCode}
                        </span>
                        {item.source && (
                          <Badge variant="outline" className="text-[9px] h-4 px-1 bg-slate-50 text-slate-700 border-slate-200 font-mono leading-none">
                            {item.source}
                          </Badge>
                        )}
                      </div>
                      <StatusBadge type="NCRStatus" value={item.status} />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1 max-w-[360px]">
                      <span
                        className="text-[12px] font-medium text-slate-900 line-clamp-1 leading-tight"
                        title={item.issueSummary}
                      >
                        {item.issueSummary}
                      </span>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {item.defectCategory6M && (
                          <Badge variant="outline" className="text-[9px] h-4 px-1 bg-indigo-50 text-indigo-700 border-indigo-200 font-mono">
                            6M: {item.defectCategory6M}
                          </Badge>
                        )}
                        {item.disposition && item.disposition !== "PENDING" && (
                          <Badge variant="outline" className={cn("text-[9px] h-4 px-1", dispositions.colorOf(item.disposition))}>
                            {dispositions.labelOf(item.disposition)}
                          </Badge>
                        )}
                        {item.defectQty > 0 && (
                          <span className="text-[10px] font-mono font-bold text-rose-600">
                            Lỗi: {formatNumber(item.defectQty)} {item.unit || ""}
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] text-slate-500 font-mono leading-none">
                        Ref: {item.relatedId || "—"} •{" "}
                        {new Date(
                          item.createdAt || Date.now(),
                        ).toLocaleDateString("vi-VN")}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1.5">
                        <User className="w-3 h-3 text-slate-400" />
                        <span className="text-[12px] font-semibold text-slate-700 leading-none">
                          {item.responsibleDept}
                        </span>
                      </div>
                      <span className="text-[11px] text-rose-600 font-mono font-medium leading-none">
                        -{formatNumber(item.costImpact || 0)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {getSeverityBadge(item.severity)}
                  </TableCell>
                  <TableCell
                    className="apv-col-actions px-4 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-7 w-7 p-0">
                          <MoreHorizontal className="h-4 w-4 text-slate-400" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[180px]">
                        <DropdownMenuLabel>Thao tác</DropdownMenuLabel>
                        {hasPermission("ncr", "update") && (
                          <DropdownMenuItem
                            onClick={() =>
                              navigate({
                                to: "/ncr/$ncrId/edit" as any,
                                params: { ncrId: itemId },
                              } as any)
                            }
                          >
                            <Activity className="w-4 h-4 mr-2" /> Chỉnh sửa /
                            CAPA
                          </DropdownMenuItem>
                        )}
                        {canCloseNcr && NCR_CLOSABLE.includes(String(item.status || "").toUpperCase()) && (
                          <DropdownMenuItem
                            className="text-emerald-600"
                            data-testid="ncr-row-close"
                            onClick={() => handleCloseNcr(itemId, item.ncrId || itemId)}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" /> Đóng NCR
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </DataTableRow>
              );
            })
          )}
        </TableBody>
        </Table>
      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />
    </ListPageShell>
  );
}
